import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface ApiResponse<T = any> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
    errors?: { [key: string]: string[] };
}

interface UserFunction {
    id: number;
    name: string;
    is_active?: boolean;
    department?: string;
    compartment?: string;
}

interface UserSignature {
    id?: number;
    type?: 'image' | 'canvas';
    has_signature: boolean;
    content?: string;
    url?: string;
    created_at?: string;
    file_size?: string;
    metadata?: any;
}

interface UserProfile {
    id: number;
    name: string;
    email: string;
    active_functions: UserFunction[];
    all_functions: UserFunction[];
    signature: UserSignature;
}

@Injectable({
    providedIn: 'root'
})
export class UserProfileService {
    private readonly baseUrl = `${environment.interop.basePath}api/registratura/user`;

    constructor(private http: HttpClient) { }

    /**
     * Get user profile with signature status
     */
    getUserProfile(): Observable<ApiResponse<UserProfile>> {
        return this.http.get<ApiResponse<UserProfile>>(`${this.baseUrl}/profile`)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Get current user signature
     */
    getUserSignature(): Observable<ApiResponse<UserSignature>> {
        return this.http.get<ApiResponse<UserSignature>>(`${this.baseUrl}/signature`)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Upload PNG signature image
     */
    uploadSignature(file: File): Observable<ApiResponse> {
        const formData = new FormData();
        formData.append('signature', file, file.name);

        // Create headers without Content-Type to let browser set boundary for multipart/form-data
        const headers = new HttpHeaders();

        return this.http.post<ApiResponse>(`${this.baseUrl}/signature/upload`, formData, { headers })
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Save canvas-drawn signature
     */
    saveCanvasSignature(signatureData: string, width?: number, height?: number): Observable<ApiResponse> {
        const payload: any = {
            signature_data: signatureData
        };

        if (width) payload.width = width;
        if (height) payload.height = height;

        return this.http.post<ApiResponse>(`${this.baseUrl}/signature/canvas`, payload)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Delete user signature
     */
    deleteSignature(): Observable<ApiResponse> {
        return this.http.delete<ApiResponse>(`${this.baseUrl}/signature`)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Get signature for specific user (admin only)
     */
    getSignatureByUserId(userId: number): Observable<ApiResponse<UserSignature>> {
        return this.http.get<ApiResponse<UserSignature>>(`${this.baseUrl}/signature/user/${userId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Log signature download for audit purposes
     */
    logSignatureDownload(): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(`${this.baseUrl}/signature/log-download`, {})
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Download signature as blob
     */
    downloadSignature(signatureUrl: string): Observable<Blob> {
        return this.http.get(signatureUrl, {
            responseType: 'blob',
            headers: new HttpHeaders({
                'Accept': 'image/png'
            })
        })
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Validate signature file before upload
     */
    validateSignatureFile(file: File): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];
        const maxSize = 2 * 1024 * 1024; // 2MB
        const allowedTypes = ['image/png'];

        // Check file type
        if (!allowedTypes.includes(file.type)) {
            errors.push('Only PNG files are allowed');
        }

        // Check file size
        if (file.size > maxSize) {
            errors.push(`File size must not exceed ${this.formatFileSize(maxSize)}`);
        }

        // Check if file is empty
        if (file.size === 0) {
            errors.push('File is empty');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Validate image dimensions
     */
    validateImageDimensions(file: File): Promise<{ isValid: boolean; errors: string[] }> {
        return new Promise((resolve) => {
            const img = new Image();
            const url = URL.createObjectURL(file);

            img.onload = () => {
                URL.revokeObjectURL(url);

                const errors: string[] = [];
                const maxDimension = 2000;

                // Check maximum dimensions
                if (img.width > maxDimension || img.height > maxDimension) {
                    errors.push(`Dimensiunile imaginii nu trebuie să depășească ${maxDimension}x${maxDimension} pixeli. Actuală: ${img.width}x${img.height}`);
                }

                // Check minimum dimensions for quality
                if (img.width < 50 || img.height < 50) {
                    errors.push('Dimensiunile imaginii trebuie să fie de cel puțin 50x50 pixeli pentru o calitate bună');
                }

                resolve({
                    isValid: errors.length === 0,
                    errors
                });
            };

            img.onerror = () => {
                URL.revokeObjectURL(url);
                resolve({
                    isValid: false,
                    errors: ['Invalid image file']
                });
            };

            img.src = url;
        });
    }

    /**
     * Format file size in human readable format
     */
    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Convert canvas to blob
     */
    canvasToBlob(canvas: HTMLCanvasElement, quality = 0.9): Promise<Blob> {
        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to convert canvas to blob'));
                }
            }, 'image/png', quality);
        });
    }

    /**
     * Resize image to fit within maximum dimensions while maintaining aspect ratio
     */
    resizeImage(file: File, maxWidth: number, maxHeight: number): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('Could not get canvas context'));
                return;
            }

            img.onload = () => {
                let { width, height } = img;

                // Calculate new dimensions while maintaining aspect ratio
                if (width > height) {
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = (width * maxHeight) / height;
                        height = maxHeight;
                    }
                }

                // Set canvas dimensions
                canvas.width = width;
                canvas.height = height;

                // Draw resized image
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to blob
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to resize image'));
                    }
                }, 'image/png', 0.9);
            };

            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };

            img.src = URL.createObjectURL(file);
        });
    }

    /**
     * Handle HTTP errors
     */
    private handleError = (error: HttpErrorResponse): Observable<never> => {
        let errorMessage = 'An unexpected error occurred';

        if (error.error instanceof ErrorEvent) {
            // Client-side error
            errorMessage = `Client Error: ${error.error.message}`;
        } else {
            // Server-side error
            if (error.status === 0) {
                errorMessage = 'Unable to connect to the server. Please check your internet connection.';
            } else if (error.status >= 400 && error.status < 500) {
                // Client errors (4xx)
                if (error.error?.message) {
                    errorMessage = error.error.message;
                } else if (error.error?.errors) {
                    const errors = Object.values(error.error.errors).flat();
                    errorMessage = errors.join(', ');
                } else {
                    errorMessage = `Client Error: ${error.status} - ${error.statusText}`;
                }
            } else if (error.status >= 500) {
                // Server errors (5xx)
                errorMessage = 'Server error. Please try again later.';
            }
        }

        console.error('UserProfileService Error:', {
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            error: error.error
        });

        return throwError(() => ({
            ...error,
            userMessage: errorMessage
        }));
    };
}